import {
  useCallback,
  useEffect,
  useState,
  useMemo,
} from 'react';
import {
  shallowEqual,
  useDispatch,
  useSelector,
} from 'react-redux';
import {
  getStatus,
} from 'redux-resource';
import {
  useNavigate,
} from 'react-router';
import {
  DateTime,
} from 'luxon';
import i18n from 'i18next';

import {
  coreActions,
  coreSelectors,
} from '@gymcloud/core';

import {
  uiActions,
} from 'web-actions';

import {
  Icon,
  Modal,
  Button,
  Avatar,
  Spinner,
  NotFound,
  ExerciseResultList,
} from 'web-components';

import {
  Flex,
  Text,
  SVG,
} from 'web-styled';

import * as S from './styles';

interface Props {
  id?: number
  onRequestClose: { (): void }
}

const WorkoutResultModal = ({
  id,
  onRequestClose,
}: Props) => {
  const navigate = useNavigate();
  const [notesVisible, setNotesVisible] = useState(true);
  const resourceType = 'workoutEvents';
  const mapState = useCallback(state => ({
    readStatus: getStatus(
      state,
      [
        `${resourceType}.requests.fetchFullEvent.status`,
        `${resourceType}.meta[${id}].readStatus`,
      ],
      false,
    ),
    event: id ? coreSelectors.getCurrentEvent(resourceType, id)(state) : {},
    exercises: id ? coreSelectors.getEventExercises(id)(state) : [],
  }), [id]);

  const {
    event,
    exercises,
    readStatus,
  } = useSelector(mapState, shallowEqual);
  const dispatch = useDispatch();

  useEffect(() => {
    if (id) {
      dispatch(
        coreActions.requestLoadFullWorkoutEvent({
          id,
        }),
      );
    }
  }, [id]);

  const isFutureDate = useMemo(() => {
    if (!event.start) return false;
    return new Date(event.start).setHours(0, 0, 0, 0) > new Date().setHours(0, 0, 0, 0);
  }, [event.start]);

  const handleDelete = useCallback(() => {
    const onConfirmAction = coreActions.requestDeleteEntity({
      id: id!,
      resourceType,
    });

    onRequestClose();
    dispatch(
      uiActions.confirmModal({
        modal: {
          title: 'Remove All results?',
          bodyText: 'Are you sure you want remove all results?',
        },
        onConfirmAction,
      }),
    );
  }, [id]);

  return (
    <Modal
      title={i18n.format(i18n.t('workout.results.title'), 'cap')}
      isOpen={!!id}
      contentStyle={{ width: 600 }}
      onRequestClose={onRequestClose}
    >
      <Flex
        column
        width="100%"
        align="flex-start"
        spacing={25}
      >
        <S.Header spacing={14}>
          <Flex spacing={14}>
            <Avatar
              src={event.avatar?.thumb?.url}
              width="80px"
              height="80px"
              borderColor="primary"
              borderWidth={3}
              placeholderSize={40}
            />
            <Flex column>
              <Text
                size="20px"
                color="#333333"
                weight={700}
                lineHeight="23px"
              >
                {event.person_name}
              </Text>
              <Text color="#5f5f5f" size="16px">
                {`${event.workout_name}${event.program_name ? ` (${event.program_name})` : ''}`}
              </Text>
              <Text color="#5f5f5f" size="16px">
                {event.start}
              </Text>
            </Flex>
          </Flex>
          <Flex>
            {isFutureDate && (
              <Button
                ghost
                spacing="large"
                appearance="primary"
                onClick={() => {
                  dispatch(
                    uiActions.setUiState({
                      newWorkoutEventModal: {
                        isOpen: true,
                        initialValues: {
                          originalId: event.id,
                          start: DateTime.fromISO(event.begins_at),
                          end: DateTime.fromISO(event.ends_at),
                          client: event.user_id,
                          workout: event.personal_workout_id,
                        },
                      },
                    }),
                  );
                  onRequestClose();
                }}
              >
                Edit
              </Button>
            )}
          </Flex>
        </S.Header>

        {readStatus.succeeded && (
          <ExerciseResultList
            exercises={exercises}
          />
        )}

        {readStatus.pending && (
          <Spinner message="Loading Event..." />
        )}

        {readStatus.failed && (
          <NotFound title={`Event Not Found (${id})`} />
        )}

        <Flex
          column
          width="100%"
        >
          <Flex
            column
            align="flex-end"
            width="100%"
          >
            <Flex
              align="center"
              spacing={5}
              clickable
              onClick={() => setNotesVisible(!notesVisible)}
            >
              <Text size="13px" color="primary">See Notes</Text>
              {notesVisible
                ? <SVG.ChevronUp size={16} strokeWidth={3} className="primary-color" />
                : <SVG.ChevronDown size={16} strokeWidth={3} className="primary-color" />
              }
            </Flex>
          </Flex>

          {notesVisible ? (
            <Text whiteSpace="pre-line">{event.note}</Text>
          ) : (
            <Text color="#ccc">
              {i18n.t('workout.results.no_notes')}
            </Text>
          )}
        </Flex>

        <Flex width="100%" justify="space-between">
          <Button
            ghost
            spacing="large"
            appearance="danger"
            onClick={handleDelete}
          >
            Delete
          </Button>
          <Button
            ghost
            spacing="large"
            appearance="primary"
            onClick={() => {
              navigate(`/events/${event.id}/results`);
            }}
          >
            Start
          </Button>
        </Flex>
      </Flex>
    </Modal>
  );
};

export default WorkoutResultModal;
